import { useMemo, type ReactNode } from "react";
import {
  startOfWeek, addWeeks, subWeeks, addDays, subDays, format, isSameDay, isToday
} from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/status-badge";
import { ChevronLeft, ChevronRight, Plus, MapPin, Clock } from "lucide-react";
import type { Profile, Client, Prospect } from "@shared/schema";
import { MISSION_TYPES } from "@shared/missionTypeConfig";
import {
  compareMissions,
  resolveMissionLocation,
  resolveMissionTierName,
  resolveMissionTitle,
} from "@shared/mission-display";

const MISSION_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(MISSION_TYPES).map(([k, v]) => [k, v.label])
);

const MISSION_TYPE_COLORS: Record<string, string> = {
  demonstration: "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",
  installation: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
  location: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  salon: "bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-800",
  atelier: "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
  formation: "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800",
  livraison: "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800",
  organisation: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-800",
  bureau: "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-800",
  salon_interne: "bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-800",
  salon_partenaire: "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
  prestation_evenement_client: "bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800",
};

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function TechAvatar({ profile }: { profile: Profile }) {
  if (profile.avatarUrl) {
    return (
      <img
        src={profile.avatarUrl}
        alt={`${profile.firstName} ${profile.lastName}`}
        className="w-5 h-5 rounded-full object-cover border border-border flex-shrink-0"
        title={`${profile.firstName} ${profile.lastName}`}
      />
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[9px] font-semibold flex-shrink-0 border border-primary/20"
      title={`${profile.firstName} ${profile.lastName}`}
    >
      {getInitials(profile.firstName, profile.lastName)}
    </span>
  );
}

function resolveMissionTiersName(
  mission: any,
  clients: Client[],
  prospects: Prospect[],
  resellers?: any[]
): string | undefined {
  const tt = mission.tiersType;
  const tid = mission.tiersId;
  if (tt === "prospect" && tid) return resolveMissionTierName(prospects?.find((p) => p.id === tid)) || undefined;
  if (tt === "client" && tid) {
    const c = clients?.find((c) => c.id === tid);
    return resolveMissionTierName(c) || undefined;
  }
  if (tt === "revendeur" && tid) {
    const r = resellers?.find((r) => r.id === tid);
    return resolveMissionTierName(r) || undefined;
  }
  if (mission.prospectId) return resolveMissionTierName(prospects?.find((p) => p.id === mission.prospectId)) || undefined;
  if (mission.clientId) {
    const c = clients?.find((c) => c.id === mission.clientId);
    return resolveMissionTierName(c) || undefined;
  }
  return undefined;
}

interface MissionCardProps {
  mission: any;
  clients: Client[];
  prospects: Prospect[];
  profiles: Profile[];
  resellers?: any[];
  onClick: () => void;
  dayBadge?: { n: number; total: number };
}

function MissionCard({ mission, clients, prospects, profiles, resellers, onClick, dayBadge }: MissionCardProps) {
  const entityName = resolveMissionTiersName(mission, clients, prospects, resellers);

  const prospect = prospects?.find((p: Prospect) => p.id === (mission.tiersType === "prospect" ? mission.tiersId : mission.prospectId));
  const client = clients?.find((c: Client) => c.id === (mission.tiersType === "client" ? mission.tiersId : mission.clientId));

  const location = resolveMissionLocation(mission, { prospect, client });
  const title = resolveMissionTitle(mission, entityName);

  const techProfiles = (mission.technicianIds || [])
    .map((id: string) => profiles?.find((p: Profile) => p.id === id))
    .filter(Boolean) as Profile[];

  const typeColor = MISSION_TYPE_COLORS[mission.missionType] || "bg-secondary text-secondary-foreground border-border";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-lg border bg-card shadow-sm hover:shadow-md hover:bg-secondary/20 dark:hover:bg-secondary/10 transition-all p-2 space-y-1.5 group cursor-pointer"
      data-testid={`week-card-${mission.id}`}
    >
      <div className="flex items-start justify-between gap-1.5">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border flex-shrink-0 ${typeColor}`}>
          {MISSION_TYPE_LABELS[mission.missionType] || mission.missionType}
        </span>
        {dayBadge && dayBadge.total > 1 && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-foreground/10 text-foreground border border-border flex-shrink-0">
            J{dayBadge.n}/{dayBadge.total}
          </span>
        )}
      </div>

      <div className="min-w-0">
        <p className="text-xs font-semibold leading-tight truncate text-foreground" title={title} data-testid={`week-card-name-${mission.id}`}>
          {title}
        </p>
        {location && (
          <p className="text-[10px] text-muted-foreground truncate mt-0.5" title={location}>{location}</p>
        )}
        {mission.heureDebut && (
          <p className="text-[10px] text-muted-foreground">
            {mission.heureDebut}{mission.heureFin ? ` → ${mission.heureFin}` : ""}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-1">
        {techProfiles.length > 0 && (
          <div className="flex items-center gap-0.5 flex-wrap">
            {techProfiles.slice(0, 3).map((p: Profile) => (
              <TechAvatar key={p.id} profile={p} />
            ))}
            {techProfiles.length > 3 && (
              <span className="text-[10px] text-muted-foreground">+{techProfiles.length - 3}</span>
            )}
          </div>
        )}
        <StatusBadge status={mission.status} type="mission" className="text-[10px] px-1.5 py-0 leading-4" />
      </div>
    </button>
  );
}

interface MissionWeekViewProps {
  weekStart: Date;
  numDays?: 5 | 7;
  onWeekChange: (date: Date) => void;
  missions: any[];
  clients: Client[];
  prospects: Prospect[];
  profiles: Profile[];
  resellers?: any[];
  onMissionClick: (mission: any) => void;
  onNewMission: (prefillDate?: Date) => void;
  onDayClick?: (day: Date) => void;
  weeklyPdfActions?: ReactNode;
}

export function MissionWeekView({
  weekStart,
  numDays = 5,
  onWeekChange,
  missions,
  clients,
  prospects,
  profiles,
  resellers,
  onMissionClick,
  onNewMission,
  onDayClick,
  weeklyPdfActions,
}: MissionWeekViewProps) {
  const days = useMemo(() => {
    return Array.from({ length: numDays }, (_, i) => addDays(weekStart, i));
  }, [weekStart, numDays]);

  const missionsByDay = useMemo(() => {
    const map = new Map<string, { mission: any; dayBadge?: { n: number; total: number } }[]>();
    for (const day of days) {
      const key = format(day, "yyyy-MM-dd");
      map.set(key, []);
    }
    for (const mission of missions || []) {
      const startDate = new Date(mission.startDate);
      const endDate = new Date(mission.endDate);
      const startMidnight = new Date(startDate); startMidnight.setHours(0, 0, 0, 0);
      const endMidnight = new Date(endDate); endMidnight.setHours(0, 0, 0, 0);
      const totalDays = Math.round((endMidnight.getTime() - startMidnight.getTime()) / 86400000) + 1;
      for (const day of days) {
        const dayStr = format(day, "yyyy-MM-dd");
        const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);
        if (startDate <= dayEnd && endDate >= dayStart) {
          const n = Math.round((dayStart.getTime() - startMidnight.getTime()) / 86400000) + 1;
          map.get(dayStr)?.push({
            mission,
            dayBadge: totalDays > 1 ? { n, total: totalDays } : undefined,
          });
        }
      }
    }
    for (const dayMissions of map.values()) {
      dayMissions.sort((a, b) => compareMissions(a.mission, b.mission));
    }
    return map;
  }, [missions, days]);

  const lastDay = days[days.length - 1];
  const weekLabel = `${format(weekStart, "d MMM", { locale: fr })} – ${format(lastDay, "d MMM yyyy", { locale: fr })}`;

  const goToPrev = () => onWeekChange(subWeeks(weekStart, 1));
  const goToNext = () => onWeekChange(addWeeks(weekStart, 1));
  const goToToday = () => {
    const today = new Date();
    onWeekChange(startOfWeek(today, { weekStartsOn: 1 }));
  };

  const isCurrentWeek = isSameDay(
    weekStart,
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  const gridClass = numDays === 5 ? "grid-cols-5" : "grid-cols-7";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPrev}
            data-testid="btn-week-prev"
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNext}
            data-testid="btn-week-next"
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-foreground" data-testid="text-week-label">
            {weekLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {weeklyPdfActions}
          <Button
            variant={isCurrentWeek ? "secondary" : "outline"}
            size="sm"
            onClick={goToToday}
            data-testid="btn-today"
            className="text-xs h-8"
          >
            Aujourd'hui
          </Button>
        </div>
      </div>

      <div className={`grid ${gridClass} gap-1 min-h-[500px]`}>
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const dayMissions = missionsByDay.get(key) || [];
          const todayClass = isToday(day)
            ? "bg-primary/5 border-primary/30 dark:bg-primary/10"
            : "bg-card border-border";

          return (
            <div
              key={key}
              className={`rounded-xl border flex flex-col min-h-[500px] overflow-hidden ${todayClass}`}
              data-testid={`week-col-${key}`}
            >
              <div className={`px-2 py-2 border-b flex items-center justify-between gap-1 ${isToday(day) ? "bg-primary/10 border-primary/20" : "bg-secondary/30 border-border"}`}>
                <button
                  type="button"
                  className={`text-left flex-1 min-w-0 ${onDayClick ? "hover:opacity-70 cursor-pointer transition-opacity" : "cursor-default"}`}
                  onClick={() => onDayClick?.(day)}
                  data-testid={`week-header-${key}`}
                  title={onDayClick ? "Voir la journée" : undefined}
                >
                  <p className={`text-[11px] font-semibold capitalize ${isToday(day) ? "text-primary" : "text-muted-foreground"}`}>
                    {format(day, "EEEE", { locale: fr })}
                  </p>
                  <p className={`text-sm font-bold leading-tight ${isToday(day) ? "text-primary" : "text-foreground"}`}>
                    {format(day, "d MMMM", { locale: fr })}
                  </p>
                </button>
                <button
                  type="button"
                  title="Nouvelle mission ce jour"
                  onClick={() => onNewMission(day)}
                  className="flex items-center justify-center w-5 h-5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors flex-shrink-0"
                  data-testid={`btn-new-mission-day-${key}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="flex-1 p-1.5 space-y-1.5 overflow-y-auto">
                {dayMissions.length === 0 ? (
                  <div className="flex items-center justify-center h-12">
                    <span className="text-[10px] text-muted-foreground/50">—</span>
                  </div>
                ) : (
                  dayMissions.map(({ mission, dayBadge }) => (
                    <MissionCard
                      key={mission.id}
                      mission={mission}
                      clients={clients}
                      prospects={prospects}
                      profiles={profiles}
                      resellers={resellers}
                      dayBadge={dayBadge}
                      onClick={() => onMissionClick(mission)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface MissionDayViewProps {
  currentDay: Date;
  onDayChange: (day: Date) => void;
  missions: any[];
  clients: Client[];
  prospects: Prospect[];
  profiles: Profile[];
  resellers?: any[];
  onMissionClick: (mission: any) => void;
  onNewMission: (prefillDate?: Date) => void;
}

export function MissionDayView({
  currentDay,
  onDayChange,
  missions,
  clients,
  prospects,
  profiles,
  resellers,
  onMissionClick,
  onNewMission,
}: MissionDayViewProps) {
  const dayKey = format(currentDay, "yyyy-MM-dd");

  const dayMissions = useMemo(() => {
    return (missions || []).filter((mission: any) => {
      const startDate = new Date(mission.startDate);
      const endDate = new Date(mission.endDate);
      const dayStart = new Date(currentDay);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(currentDay);
      dayEnd.setHours(23, 59, 59, 999);
      return startDate <= dayEnd && endDate >= dayStart;
    }).sort(compareMissions);
  }, [missions, currentDay]);

  const dayLabel = format(currentDay, "EEEE d MMMM yyyy", { locale: fr });
  const isCurrentDay = isToday(currentDay);

  const goToPrev = () => onDayChange(subDays(currentDay, 1));
  const goToNext = () => onDayChange(addDays(currentDay, 1));
  const goToToday = () => onDayChange(new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 bg-card border rounded-xl p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToPrev} className="h-8 w-8 p-0" data-testid="btn-day-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToNext} className="h-8 w-8 p-0" data-testid="btn-day-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span
            className={`text-base font-semibold capitalize ${isCurrentDay ? "text-primary" : "text-foreground"}`}
            data-testid="text-day-label"
          >
            {dayLabel}
          </span>
          {isCurrentDay && (
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Aujourd'hui</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={isCurrentDay ? "secondary" : "outline"}
            size="sm"
            onClick={goToToday}
            data-testid="btn-today"
            className="text-xs h-8"
          >
            Aujourd'hui
          </Button>
          <Button
            size="sm"
            onClick={() => onNewMission(currentDay)}
            className="h-8 text-xs gap-1.5"
            data-testid={`btn-new-mission-day-${dayKey}`}
          >
            <Plus className="h-3.5 w-3.5" />
            Nouvelle mission
          </Button>
        </div>
      </div>

      {dayMissions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground rounded-xl border bg-card">
          <p className="text-sm font-medium mb-1">Aucune mission ce jour</p>
          <p className="text-xs">Cliquez sur "Nouvelle mission" pour en créer une.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {dayMissions.map((mission: any) => {
            const entityName = resolveMissionTiersName(mission, clients, prospects, resellers);
            const prospect = prospects?.find((p: Prospect) => p.id === (mission.tiersType === "prospect" ? mission.tiersId : mission.prospectId));
            const client = clients?.find((c: Client) => c.id === (mission.tiersType === "client" ? mission.tiersId : mission.clientId));

            const location = resolveMissionLocation(mission, { prospect, client });
            const title = resolveMissionTitle(mission, entityName);

            const techProfiles = (mission.technicianIds || [])
              .map((id: string) => profiles?.find((p: Profile) => p.id === id))
              .filter(Boolean) as Profile[];

            const typeColor = MISSION_TYPE_COLORS[mission.missionType] || "bg-secondary text-secondary-foreground border-border";

            // Badge J{n}/{total} pour missions multi-jours
            const mStartMidnight = new Date(mission.startDate); mStartMidnight.setHours(0, 0, 0, 0);
            const mEndMidnight = new Date(mission.endDate); mEndMidnight.setHours(0, 0, 0, 0);
            const mTotalDays = Math.round((mEndMidnight.getTime() - mStartMidnight.getTime()) / 86400000) + 1;
            const currentDayMidnight = new Date(currentDay); currentDayMidnight.setHours(0, 0, 0, 0);
            const mDayN = Math.round((currentDayMidnight.getTime() - mStartMidnight.getTime()) / 86400000) + 1;

            return (
              <button
                key={mission.id}
                type="button"
                onClick={() => onMissionClick(mission)}
                className="w-full text-left rounded-xl border bg-card shadow-sm hover:shadow-md hover:bg-secondary/20 transition-all p-4 space-y-3 group cursor-pointer"
                data-testid={`day-card-${mission.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border flex-shrink-0 ${typeColor}`}>
                      {MISSION_TYPE_LABELS[mission.missionType] || mission.missionType}
                    </span>
                    {mTotalDays > 1 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-foreground/10 text-foreground border border-border flex-shrink-0">
                        J{mDayN}/{mTotalDays}
                      </span>
                    )}
                    <h3 className="text-sm font-bold text-foreground truncate" title={title} data-testid={`day-card-name-${mission.id}`}>
                      {title}
                    </h3>
                  </div>
                  <StatusBadge status={mission.status} type="mission" className="text-xs flex-shrink-0" />
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {location && (
                    <div className="flex items-start gap-2 col-span-2 sm:col-span-1">
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground text-xs truncate" title={location}>{location}</span>
                    </div>
                  )}

                  {(mission.heureDebut || mission.arrivalTime) && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-muted-foreground">
                        {mission.arrivalTime && `Arrivée ${mission.arrivalTime}`}
                        {mission.heureDebut && !mission.arrivalTime && `${mission.heureDebut}${mission.heureFin ? ` → ${mission.heureFin}` : ""}`}
                      </span>
                    </div>
                  )}
                </div>

                {techProfiles.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      {techProfiles.map((p: Profile) => (
                        <TechAvatar key={p.id} profile={p} />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {techProfiles.map((p: Profile) => `${p.firstName} ${p.lastName}`).join(", ")}
                    </span>
                  </div>
                )}

                {mission.description && (
                  <p className="text-xs text-muted-foreground border-t pt-2 line-clamp-2">
                    {mission.description}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
